<template>
  <div class="stock-document-files">
    <v-subheader class="px-0">{{ $t("STOCK.DOCUMENTS.TITLE") }}</v-subheader>

    <div v-if="showSystemDocumentsSection" class="mb-4">
      <div class="stock-document-files__section-head">
        <div class="stock-document-files__section-label">
          {{ $t("STOCK.DOCUMENTS.SYSTEM_DOCUMENTS") }}
        </div>
        <v-btn
          v-if="canDownloadAllGenerated"
          small
          text
          :loading="isDownloadingAll"
          @click="downloadAllGenerated"
        >
          <v-icon small left>mdi-download-multiple</v-icon>
          {{ $t("STOCK.DOCUMENTS.DOWNLOAD_ALL") }}
        </v-btn>
      </div>
      <v-card outlined class="pa-2">
        <div v-if="showOwnDocumentGroup" class="stock-document-files__group">
          <div v-if="showOwnGroupHeading" class="stock-document-files__group-label">
            {{ $t("STOCK.DOCUMENTS.TYPE_TRANSPORT_ORDER") }}
          </div>

          <div
            v-for="file in generatedFiles"
            :key="generatedFileKey(file)"
            class="stock-document-files__row"
          >
            <div class="stock-document-files__name">
              <v-icon small color="primary" class="mr-2">{{ generatedFileIcon(file) }}</v-icon>
              <div class="stock-document-files__name-text">
                <span>{{ file.original_name || file.name }}</span>
                <div v-if="!showOwnGroupHeading" class="stock-document-files__chips">
                  <v-chip x-small color="primary" outlined>
                    {{ generatedFileTypeLabel(file) }}
                  </v-chip>
                </div>
              </div>
            </div>
            <div class="stock-document-files__actions">
              <span v-if="file.generated_at" class="stock-document-files__meta mr-3">
                {{ formatGeneratedAt(file.generated_at) }}
              </span>
              <span class="stock-document-files__meta mr-2">{{ formatFileSize(file.size) }}</span>
              <v-btn
                v-if="canRegenerateOwnDocument && isPrimaryOwnDocument(file)"
                small
                outlined
                color="primary"
                class="mr-1"
                :loading="isRegenerating"
                :aria-label="generateButtonLabel"
                @click="regenerateDocument"
              >
                <v-icon small left>mdi-refresh</v-icon>
                {{ generateButtonLabel }}
              </v-btn>
              <v-btn
                icon
                small
                color="primary"
                :loading="downloadingFileKey === generatedFileKey(file)"
                :disabled="isDownloadingAll"
                :aria-label="$t('GENERAL.LABELS.DOWNLOAD')"
                @click="downloadFile(file)"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
            </div>
          </div>

          <div
            v-if="!generatedFiles.length"
            class="stock-document-files__row stock-document-files__row--empty"
          >
            <div class="stock-document-files__name caption grey--text">
              {{ ownDocumentEmptyLabel }}
            </div>
            <div v-if="canRegenerateOwnDocument" class="stock-document-files__actions">
              <v-btn
                small
                color="primary"
                outlined
                :loading="isRegenerating"
                @click="regenerateDocument"
              >
                <v-icon small left>mdi-refresh</v-icon>
                {{ generateButtonLabel }}
              </v-btn>
            </div>
          </div>
        </div>

        <div
          v-for="group in relatedFileGroups"
          :key="group.key"
          class="stock-document-files__group"
        >
          <div class="stock-document-files__group-label">{{ group.label }}</div>
          <div
            v-for="file in group.files"
            :key="generatedFileKey(file)"
            class="stock-document-files__row"
          >
            <div class="stock-document-files__name">
              <v-icon small color="primary" class="mr-2">{{ generatedFileIcon(file) }}</v-icon>
              <div class="stock-document-files__name-text">
                <span>{{ file.original_name || file.name }}</span>
                <div class="stock-document-files__chips">
                  <v-chip x-small color="primary" outlined>
                    {{ generatedFileTypeLabel(file) }}
                  </v-chip>
                </div>
              </div>
            </div>
            <div class="stock-document-files__actions">
              <span v-if="file.generated_at" class="stock-document-files__meta mr-3">
                {{ formatGeneratedAt(file.generated_at) }}
              </span>
              <span v-if="!file.pending" class="stock-document-files__meta mr-2">{{ formatFileSize(file.size) }}</span>
              <v-btn
                v-if="file.pending && canGenerateDeliveryNote"
                small
                outlined
                color="primary"
                :loading="downloadingFileKey === generatedFileKey(file)"
                :disabled="isDownloadingAll"
                @click="downloadFile(file)"
              >
                <v-icon small left>mdi-file-pdf-box</v-icon>
                {{ $t("STOCK.DOCUMENTS.GENERATE_PDF") }}
              </v-btn>
              <span v-else-if="file.pending" class="stock-document-files__meta">
                {{ $t("STOCK.DOCUMENTS.NOT_GENERATED") }}
              </span>
              <template v-else>
                <v-btn
                  v-if="canRegenerateDeliveryNote(file)"
                  icon
                  small
                  color="primary"
                  class="mr-1"
                  :loading="regeneratingFileKey === generatedFileKey(file)"
                  :disabled="isDownloadingAll || downloadingFileKey !== null"
                  :aria-label="$t('STOCK.DOCUMENTS.REGENERATE')"
                  @click="regenerateDeliveryNote(file)"
                >
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="downloadingFileKey === generatedFileKey(file)"
                  :disabled="isDownloadingAll"
                  :aria-label="$t('GENERAL.LABELS.DOWNLOAD')"
                  @click="downloadFile(file)"
                >
                  <v-icon small>mdi-download</v-icon>
                </v-btn>
              </template>
            </div>
          </div>
        </div>

        <div
          v-if="showRelatedFilesHint"
          class="caption grey--text pa-2"
        >
          {{ $t("STOCK.DOCUMENTS.RELATED_SHIPMENT_FILES_HINT") }}
        </div>
      </v-card>
    </div>

    <div class="stock-document-files__section-label">
      {{ $t("STOCK.DOCUMENTS.ATTACHMENTS") }}
    </div>

    <div
      v-if="canEditFiles"
      class="stock-document-files__dropzone mb-3"
      :class="{ 'stock-document-files__dropzone--active': isDropzoneActive }"
      @click="openFilePicker"
      @dragenter.prevent="isDropzoneActive = true"
      @dragover.prevent="isDropzoneActive = true"
      @dragleave.prevent="isDropzoneActive = false"
      @drop.prevent="onDropFiles"
    >
      <v-icon color="primary" class="mr-2">mdi-tray-arrow-up</v-icon>
      <span>{{ $t("STOCK.DOCUMENTS.DROP_FILES_HINT") }}</span>
      <input
        ref="filesInput"
        type="file"
        class="d-none"
        multiple
        @change="onFileInputChange"
      />
    </div>

    <v-progress-linear v-if="isUploading" indeterminate color="primary" class="mb-3" />

    <v-card v-if="uploadedFiles.length" outlined class="pa-2">
      <div
        v-for="file in uploadedFiles"
        :key="`upload-${file.id}`"
        class="stock-document-files__row"
      >
        <div class="stock-document-files__name">
          <v-icon small class="mr-2">mdi-file-outline</v-icon>
          <span>{{ file.original_name || file.name }}</span>
        </div>
        <div class="stock-document-files__actions">
          <span class="stock-document-files__meta mr-3">{{ formatFileSize(file.size) }}</span>
          <v-btn
            icon
            small
            color="primary"
            :aria-label="$t('GENERAL.LABELS.DOWNLOAD')"
            @click="downloadFile(file)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn
            v-if="canEditFiles"
            icon
            small
            color="error"
            :loading="deletingFileId === file.id"
            :disabled="deletingFileId !== null"
            :aria-label="$t('GENERAL.LABELS.DELETE')"
            @click="deleteFile(file.id)"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
    <div v-else-if="!canEditFiles" class="caption grey--text">
      {{ $t("STOCK.DOCUMENTS.NO_ATTACHMENTS") }}
    </div>
  </div>
</template>

<script>
import { SHOW_SNACK } from "@/store/snack.module";
import {
  UPLOAD_STOCK_PO_FILES,
  DELETE_STOCK_PO_FILE,
  DOWNLOAD_STOCK_PO_FILE,
  REGENERATE_STOCK_PO_DOCUMENT,
} from "@/store/stock/stockPurchaseOrders.module";
import {
  UPLOAD_STOCK_RECEIPT_FILES,
  DELETE_STOCK_RECEIPT_FILE,
  DOWNLOAD_STOCK_RECEIPT_FILE,
  REGENERATE_STOCK_RECEIPT_DOCUMENT,
} from "@/store/stock/stockReceipts.module";
import {
  UPLOAD_STOCK_SO_FILES,
  DELETE_STOCK_SO_FILE,
  DOWNLOAD_STOCK_SO_FILE,
  REGENERATE_STOCK_SO_DOCUMENT,
} from "@/store/stock/stockSalesOrders.module";
import {
  UPLOAD_STOCK_SALE_FILES,
  DELETE_STOCK_SALE_FILE,
  DOWNLOAD_STOCK_SALE_FILE,
  REGENERATE_STOCK_SALE_DOCUMENT,
} from "@/store/stock/stockSales.module";
import {
  UPLOAD_STOCK_WRITE_OFF_FILES,
  DELETE_STOCK_WRITE_OFF_FILE,
  DOWNLOAD_STOCK_WRITE_OFF_FILE,
  REGENERATE_STOCK_WRITE_OFF_DOCUMENT,
} from "@/store/stock/stockWriteOffs.module";
import {
  UPLOAD_STOCK_TRANSFER_FILES,
  DELETE_STOCK_TRANSFER_FILE,
  DOWNLOAD_STOCK_TRANSFER_FILE,
} from "@/store/stock/stockTransfers.module";
import {
  UPLOAD_STOCK_TO_FILES,
  DELETE_STOCK_TO_FILE,
  DOWNLOAD_STOCK_TO_FILE,
  REGENERATE_STOCK_TO_DOCUMENT,
} from "@/store/stock/stockTransportOrders.module";
import {
  UPLOAD_STOCK_INVOICE_FILES,
  DELETE_STOCK_INVOICE_FILE,
  DOWNLOAD_STOCK_INVOICE_FILE,
  REGENERATE_STOCK_INVOICE_DOCUMENT,
} from "@/store/stock/stockInvoices.module";
import { DOWNLOAD_STOCK_SHIPMENT_FILE } from "@/store/stock/stockShipments.module";
import {
  UPLOAD_STOCK_DELIVERY_NOTE_FILES,
  DELETE_STOCK_DELIVERY_NOTE_FILE,
  DOWNLOAD_STOCK_DELIVERY_NOTE_FILE,
  REGENERATE_STOCK_DELIVERY_NOTE_DOCUMENT,
} from "@/store/stock/stockDeliveryNotes.module";

const DOCUMENT_CONFIG = {
  purchase_order: {
    upload: UPLOAD_STOCK_PO_FILES,
    delete: DELETE_STOCK_PO_FILE,
    download: DOWNLOAD_STOCK_PO_FILE,
    regenerate: REGENERATE_STOCK_PO_DOCUMENT,
    editPermission: "stock-purchase-orders-edit",
  },
  receipt: {
    upload: UPLOAD_STOCK_RECEIPT_FILES,
    delete: DELETE_STOCK_RECEIPT_FILE,
    download: DOWNLOAD_STOCK_RECEIPT_FILE,
    regenerate: REGENERATE_STOCK_RECEIPT_DOCUMENT,
    editPermission: "stock-receipts-edit",
  },
  sales_order: {
    upload: UPLOAD_STOCK_SO_FILES,
    delete: DELETE_STOCK_SO_FILE,
    download: DOWNLOAD_STOCK_SO_FILE,
    regenerate: REGENERATE_STOCK_SO_DOCUMENT,
    editPermission: "stock-sales-orders-edit",
  },
  sale: {
    upload: UPLOAD_STOCK_SALE_FILES,
    delete: DELETE_STOCK_SALE_FILE,
    download: DOWNLOAD_STOCK_SALE_FILE,
    regenerate: REGENERATE_STOCK_SALE_DOCUMENT,
    editPermission: "stock-sales-edit",
  },
  write_off: {
    upload: UPLOAD_STOCK_WRITE_OFF_FILES,
    delete: DELETE_STOCK_WRITE_OFF_FILE,
    download: DOWNLOAD_STOCK_WRITE_OFF_FILE,
    regenerate: REGENERATE_STOCK_WRITE_OFF_DOCUMENT,
    editPermission: "stock-write-offs-edit",
  },
  transfer: {
    upload: UPLOAD_STOCK_TRANSFER_FILES,
    delete: DELETE_STOCK_TRANSFER_FILE,
    download: DOWNLOAD_STOCK_TRANSFER_FILE,
    editPermission: "stock-transfers-edit",
  },
  transport_order: {
    upload: UPLOAD_STOCK_TO_FILES,
    delete: DELETE_STOCK_TO_FILE,
    download: DOWNLOAD_STOCK_TO_FILE,
    regenerate: REGENERATE_STOCK_TO_DOCUMENT,
    editPermission: "stock-transport-orders-edit",
  },
  delivery_note: {
    upload: UPLOAD_STOCK_DELIVERY_NOTE_FILES,
    delete: DELETE_STOCK_DELIVERY_NOTE_FILE,
    download: DOWNLOAD_STOCK_DELIVERY_NOTE_FILE,
    regenerate: REGENERATE_STOCK_DELIVERY_NOTE_DOCUMENT,
    editPermission: "stock-delivery-notes-edit",
  },
  invoice: {
    upload: UPLOAD_STOCK_INVOICE_FILES,
    delete: DELETE_STOCK_INVOICE_FILE,
    download: DOWNLOAD_STOCK_INVOICE_FILE,
    regenerate: REGENERATE_STOCK_INVOICE_DOCUMENT,
    editPermission: "stock-invoices-edit",
  },
};

export default {
  name: "StockDocumentFilesPanel",
  props: {
    documentKind: {
      type: String,
      required: true,
      validator: (value) =>
        [
          "purchase_order",
          "receipt",
          "sales_order",
          "sale",
          "write_off",
          "transfer",
          "transport_order",
          "invoice",
        ].includes(value),
    },
    documentId: {
      type: [Number, String],
      default: null,
    },
    files: {
      type: Array,
      default: () => [],
    },
    canRegenerate: {
      type: Boolean,
      default: false,
    },
    relatedFiles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isDropzoneActive: false,
      isUploading: false,
      isRegenerating: false,
      isDownloadingAll: false,
      downloadingFileKey: null,
      regeneratingFileKey: null,
      deletingFileId: null,
    };
  },
  computed: {
    config() {
      return DOCUMENT_CONFIG[this.documentKind] || {};
    },
    canEditFiles() {
      return (
        !!this.documentId &&
        this.hasPermissions(this.config.editPermission)
      );
    },
    generatedFiles() {
      return (this.files || []).filter(
        (file) => file.source === "generated" || file.document_type
      );
    },
    displayedGeneratedFiles() {
      return [...this.generatedFiles, ...(this.relatedFiles || [])];
    },
    relatedFileGroups() {
      const groups = [];
      const byKey = {};
      (this.relatedFiles || []).forEach((file) => {
        const key = file.related_group
          || (file.shipment_id ? `shipment-${file.shipment_id}` : "other");
        if (!byKey[key]) {
          byKey[key] = {
            key,
            label: file.related_label || this.$t("STOCK.SHIPMENTS.TITLE_ITEM", { no: file.shipment_no }),
            files: [],
          };
          groups.push(byKey[key]);
        }
        byKey[key].files.push(file);
      });
      return groups;
    },
    uploadedFiles() {
      return (this.files || []).filter(
        (file) => file.source !== "generated" && !file.document_type
      );
    },
    showSystemDocumentsSection() {
      return this.displayedGeneratedFiles.length > 0 || this.canRegenerate;
    },
    showOwnDocumentGroup() {
      return this.generatedFiles.length > 0 || this.canRegenerate;
    },
    showOwnGroupHeading() {
      return this.documentKind === "transport_order" && this.relatedFileGroups.length > 0;
    },
    canRegenerateOwnDocument() {
      return this.canRegenerate && this.canEditFiles;
    },
    canDownloadAllGenerated() {
      return this.displayedGeneratedFiles.length > 1;
    },
    canGenerateDeliveryNote() {
      return this.hasPermissions("stock-delivery-notes-edit");
    },
    showRelatedFilesHint() {
      return this.documentKind === "transport_order" && !(this.relatedFiles || []).length;
    },
    ownDocumentEmptyLabel() {
      if (this.documentKind === "transport_order") {
        return this.$t("STOCK.DOCUMENTS.NO_TRANSPORT_ORDER_DOCUMENT");
      }
      return this.$t("STOCK.DOCUMENTS.NO_SYSTEM_DOCUMENT");
    },
    generateButtonLabel() {
      if (this.documentKind === "transport_order") {
        return this.generatedFiles.length
          ? this.$t("STOCK.DOCUMENTS.REGENERATE_TRANSPORT_ORDER")
          : this.$t("STOCK.DOCUMENTS.GENERATE_TRANSPORT_ORDER");
      }
      return this.generatedFiles.length
        ? this.$t("STOCK.DOCUMENTS.REGENERATE")
        : this.$t("STOCK.DOCUMENTS.GENERATE_PDF");
    },
  },
  methods: {
    openFilePicker() {
      if (!this.canEditFiles || this.isUploading) {
        return;
      }
      this.$refs.filesInput?.click();
    },
    onFileInputChange(event) {
      const selected = Array.from(event?.target?.files || []);
      if (!selected.length) {
        return;
      }
      this.uploadFiles(selected).finally(() => {
        if (this.$refs.filesInput) {
          this.$refs.filesInput.value = "";
        }
      });
    },
    onDropFiles(event) {
      this.isDropzoneActive = false;
      const selected = Array.from(event?.dataTransfer?.files || []);
      if (!selected.length) {
        return;
      }
      this.uploadFiles(selected);
    },
    uploadFiles(selectedFiles) {
      if (!this.documentId || !selectedFiles.length) {
        return Promise.resolve();
      }

      this.isUploading = true;

      return this.$store
        .dispatch(this.config.upload, {
          id: this.documentId,
          files: selectedFiles,
        })
        .then((uploadedFiles) => {
          this.$emit("files-updated", [...(this.files || []), ...uploadedFiles]);
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isUploading = false;
        });
    },
    deleteFile(fileId) {
      if (!this.documentId || !fileId || this.deletingFileId !== null) {
        return;
      }

      this.deletingFileId = fileId;

      this.$store
        .dispatch(this.config.delete, {
          id: this.documentId,
          fileId,
        })
        .then(() => {
          this.$emit(
            "files-updated",
            (this.files || []).filter((file) => Number(file.id) !== Number(fileId))
          );
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.deletingFileId = null;
        });
    },
    generatedFileKey(file) {
      if (file?.pending && file.delivery_note_id) {
        return `delivery-note-pending-${file.delivery_note_id}`;
      }
      if (file?.delivery_note_id) {
        return `delivery-note-${file.delivery_note_id}-${file.id}`;
      }
      if (file?.shipment_id) {
        return `shipment-${file.shipment_id}-${file.id}`;
      }
      return `generated-${file?.id}`;
    },
    isPrimaryOwnDocument(file) {
      return !!file && this.generatedFiles[0] && this.generatedFiles[0].id === file.id;
    },
    generatedFileTypeLabel(file) {
      if (file?.related_kind === "cmr") {
        return this.$t("STOCK.SHIPMENTS.TAB_CMR");
      }
      if (file?.related_kind === "trade") {
        return this.$t("STOCK.SHIPMENTS.TAB_TRADE_DOCUMENT");
      }
      if (file?.related_kind === "delivery_note") {
        return this.$t("STOCK.DOCUMENTS.TYPE_DELIVERY_NOTE");
      }
      if (this.documentKind === "transport_order") {
        return this.$t("STOCK.DOCUMENTS.TYPE_TRANSPORT_ORDER");
      }
      return this.$t("STOCK.DOCUMENTS.GENERATED");
    },
    downloadFile(file) {
      if (!file?.id && !file?.pending) {
        return Promise.resolve();
      }

      const fileKey = this.generatedFileKey(file);
      this.downloadingFileKey = fileKey;

      if (file.pending && !this.canGenerateDeliveryNote) {
        this.downloadingFileKey = null;
        return Promise.resolve();
      }

      const prepare = file.pending ? this.generateDeliveryNoteFile(file) : Promise.resolve(file);

      return prepare
        .then((readyFile) => {
          if (!readyFile?.id || readyFile.pending) {
            return null;
          }

          const action = this.downloadActionFor(readyFile);
          const id = this.downloadOwnerId(readyFile);
          if (!action || !id) {
            return null;
          }

          return this.$store.dispatch(action, {
            id,
            fileId: readyFile.id,
            originalName: readyFile.original_name,
            name: readyFile.name,
          });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          if (this.downloadingFileKey === fileKey) {
            this.downloadingFileKey = null;
          }
        });
    },
    downloadActionFor(file) {
      if (file?.delivery_note_id) {
        return DOWNLOAD_STOCK_DELIVERY_NOTE_FILE;
      }
      if (file?.shipment_id) {
        return DOWNLOAD_STOCK_SHIPMENT_FILE;
      }
      return this.config.download;
    },
    downloadOwnerId(file) {
      if (file?.delivery_note_id) {
        return file.delivery_note_id;
      }
      if (file?.shipment_id) {
        return file.shipment_id;
      }
      return this.documentId;
    },
    canRegenerateDeliveryNote(file) {
      return (
        file?.related_kind === "delivery_note" &&
        !file.pending &&
        this.hasPermissions("stock-delivery-notes-edit")
      );
    },
    regenerateDeliveryNote(file) {
      if (!file?.delivery_note_id || this.regeneratingFileKey) {
        return;
      }

      const fileKey = this.generatedFileKey(file);
      this.regeneratingFileKey = fileKey;

      this.generateDeliveryNoteFile(file)
        .then((readyFile) => {
          if (!readyFile) {
            return;
          }
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          if (this.regeneratingFileKey === fileKey) {
            this.regeneratingFileKey = null;
          }
        });
    },
    generateDeliveryNoteFile(file) {
      if (!file?.delivery_note_id) {
        return Promise.resolve(null);
      }

      return this.$store
        .dispatch(REGENERATE_STOCK_DELIVERY_NOTE_DOCUMENT, file.delivery_note_id)
        .then((note) => {
          this.$emit("delivery-note-updated", note);
          const generated = (note?.files || []).find(
            (item) => item && item.document_type === "delivery_note_pdf"
          );
          if (!generated) {
            return null;
          }
          return {
            ...generated,
            delivery_note_id: file.delivery_note_id,
            related_kind: "delivery_note",
          };
        });
    },
    async downloadAllGenerated() {
      if (this.isDownloadingAll || !this.canDownloadAllGenerated) {
        return;
      }

      this.isDownloadingAll = true;
      try {
        for (const file of this.displayedGeneratedFiles) {
          await this.downloadFile(file);
          await new Promise((resolve) => setTimeout(resolve, 350));
        }
      } finally {
        this.isDownloadingAll = false;
      }
    },
    regenerateDocument() {
      if (!this.documentId || !this.canRegenerate || this.isRegenerating) {
        return;
      }

      this.isRegenerating = true;

      this.$store
        .dispatch(this.config.regenerate, this.documentId)
        .then((payload) => {
          this.$emit("files-updated", payload.files || []);
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isRegenerating = false;
        });
    },
    formatFileSize(sizeInBytes) {
      const size = Number(sizeInBytes);
      if (!Number.isFinite(size) || size <= 0) {
        return "0 B";
      }
      const units = ["B", "KB", "MB", "GB"];
      const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
      const value = size / 1024 ** unitIndex;
      return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    },
    formatGeneratedAt(value) {
      if (!value) {
        return "";
      }
      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    },
    generatedFileIcon(file) {
      const extension = String(file?.extension || "").toLowerCase();
      if (extension === "docx") {
        return "mdi-file-word";
      }
      if (extension === "xlsx") {
        return "mdi-file-excel";
      }
      return "mdi-file-pdf-box";
    },
    getErrorMessage(error) {
      if (!error) {
        return this.$t("GENERAL.UNEXPECTED_ERROR");
      }
      if (typeof error === "string") {
        return error;
      }
      if (error.message) {
        return error.message;
      }
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0];
        if (firstKey && error.errors[firstKey][0]) {
          return error.errors[firstKey][0];
        }
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
  },
};
</script>

<style lang="scss" scoped>
.stock-document-files__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.stock-document-files__section-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 8px;
}

.stock-document-files__section-head .stock-document-files__section-label {
  margin-bottom: 0;
}

.stock-document-files__group + .stock-document-files__group {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 8px;
  padding-top: 8px;
}

.stock-document-files__group-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.72);
  padding: 2px 4px 6px;
}

.stock-document-files__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 6px 4px;
}

.stock-document-files__group .stock-document-files__row + .stock-document-files__row {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.stock-document-files__row--empty {
  min-height: 40px;
}

.stock-document-files__name {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.stock-document-files__name-text {
  min-width: 0;
  flex: 1;
}

.stock-document-files__name-text > span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-document-files__name > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-document-files__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
}

.stock-document-files__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
}

.stock-document-files__meta {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
}

.stock-document-files__dropzone {
  border: 1px dashed rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.6);
}

.stock-document-files__dropzone--active {
  border-color: var(--v-primary-base);
  background: rgba(25, 118, 210, 0.06);
}
</style>
